import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from './LanguageToggle.js';
import { useSession } from '../lib/session.js';
import { useVenues } from '../hooks/useVenue.js';

/**
 * The admin chrome.
 *
 * Every spacing utility here is logical (ms/me/ps/pe/start/end), so the whole
 * shell mirrors when <html dir> flips. Nothing in this file knows or asks which
 * direction it is in — which is the point.
 *
 * Navigation is filtered by permission at the ACTIVE venue: a user who manages
 * West Bay and hosts at The Pearl sees a different menu depending on which
 * venue is selected, because that is what their access actually is.
 */
const NAV: Array<{ to: string; key: string; permission: string }> = [
  { to: '/floor', key: 'floor.title', permission: 'floor:read' },
  { to: '/timeline', key: 'timeline.title', permission: 'floor:read' },
  { to: '/reservations', key: 'reservations.title', permission: 'reservation:read' },
  { to: '/waitlist', key: 'waitlist.title', permission: 'waitlist:read' },
  { to: '/guests', key: 'guests.title', permission: 'guest:read' },
  { to: '/pre-shift', key: 'preShift.title', permission: 'reservation:read' },
  { to: '/inbox', key: 'inbox.title', permission: 'message:read' },
  { to: '/reports', key: 'reports.title', permission: 'report:read' },
  { to: '/settings', key: 'settings.title', permission: 'floor:read' },
  { to: '/branch', key: 'branch.title', permission: 'floor:configure' },
  { to: '/corporate', key: 'corporate.title', permission: 'guest:read' },
  { to: '/users', key: 'users.title', permission: 'user:manage' },
];

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { can, signOut, venueId, setVenue, user } = useSession();
  const { data: venues } = useVenues();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white print:hidden">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2">
          {/* The mark carries the Arabic calligraphy, so it needs no mirroring -
              it reads correctly in both directions. */}
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-9 w-auto shrink-0"
            width={262}
            height={300}
          />

          {venues && venues.length > 1 ? (
            <select
              value={venueId ?? ''}
              onChange={(event) => setVenue(event.target.value)}
              aria-label={t('auth.venue')}
              className="rounded-md border border-slate-200 px-2 py-1 text-sm"
            >
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {i18n.language === 'ar' ? venue.name_ar : venue.name_en}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm font-semibold text-brand-bronze-dark">
              {venues?.[0]
                ? i18n.language === 'ar'
                  ? venues[0].name_ar
                  : venues[0].name_en
                : t('app.name')}
            </span>
          )}

          <div className="ms-auto flex items-center gap-2">
            <LanguageToggle />
            <span className="hidden text-sm text-slate-500 sm:inline">{user?.name}</span>
            <button
              type="button"
              onClick={signOut}
              className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
            >
              {t('auth.signOut')}
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-4 pb-2">
          {NAV.filter((item) => can(item.permission)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-teal text-white'
                    : 'text-slate-600 hover:bg-brand-teal-light',
                ].join(' ')
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-5 print:px-0 print:py-0">
        <Outlet />
      </main>
    </div>
  );
}
